import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom'
import { createBrowserHistory } from 'history'
import { Router, Route, Switch, BrowserRouter } from 'react-router-dom'

import 'bootstrap/dist/css/bootstrap.css'
import 'assets/scss/zest-admin.css'
import 'assets/fonts/simple-line-icons.css'
import 'assets/scss/WonE.scss'
import 'react-toastify/dist/ReactToastify.css'
import 'assets/scss/multiselect.css'
import 'assets/scss/WoEOverride.scss'

import { ToastContainer } from 'react-toastify'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  ApolloProvider,
  Observable,
  createHttpLink,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import allReducers from '../src/redux/reducer/index.jsx'
import { Provider } from 'react-redux'
import thunk from 'redux-thunk'
import { createStore, applyMiddleware } from 'redux'
import Loader from '../src/components/common/Loader/Loader'
import Protected from '../src/components/common/Protected'
import FallbackLoading from '../src/components/common/FallbackLoading'

import { AuthProvider } from './auth/AuthProvider'
import { getAccessToken, refreshAccessToken, isRefreshableAuthError } from './auth/tokens'

const LoginPage = lazy(() => import('layouts/LoginPage'))
const WonELayout = lazy(() => import('layouts/WonE'))
const AuthCallback = lazy(() =>
  import('./views/general/Pages/AuthCallback')
)
const NotFound = lazy(() => import('../src/components/common/NotFound'))

const store = createStore(allReducers, applyMiddleware(thunk))

const hist = createBrowserHistory()
const httpLink = createHttpLink({
  uri: process.env.REACT_APP_API_URL,
})

// WONE-568: send the OAuth access token as Bearer. The legacy
// ``localStorage.WonEToken`` + ``JWT <token>`` path is gone — the
// AuthProvider now owns the access token in memory and the refresh
// token lives in the HttpOnly ``__Secure-wone_refresh`` cookie.
const authLink = setContext((_, { headers }) => {
  const token = getAccessToken()
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }
})

// WONE-568: on 401 from /wone-admin/graphql, try one refresh round
// and retry the operation with the new token. If the refresh fails,
// clear the session and let the auth gate redirect to /. Ports the
// pattern from frontend/web/src/providers/apollo-client.js.
const refreshOnUnauthorized = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const status =
    (networkError && (networkError.statusCode || networkError.status)) || 0
  if (status !== 401) {
    // Some Django setups surface token expiry as a GraphQL error rather
    // than an HTTP 401. Treat ONLY the token-missing/expiry messages as
    // refreshable (see isRefreshableAuthError) — NOT "You do not have
    // permission" / "Permission denied", which are authorization
    // failures (the token is valid, the user just lacks access), so
    // refreshing can't fix it and retrying would loop.
    const looksLikeAuthError =
      Array.isArray(graphQLErrors) &&
      graphQLErrors.some((err) => isRefreshableAuthError(err && err.message))
    if (!looksLikeAuthError) return
  }

  // Retry-once guard: if the operation already rode a refresh and still
  // came back 401, the new token is no good — surface the error instead
  // of looping refresh → retry → 401 → refresh forever.
  if (operation.getContext().__didRefreshRetry) {
    return
  }

  return new Observable((observer) => {
    refreshAccessToken()
      .then((newToken) => {
        if (!newToken) {
          observer.error(networkError || (graphQLErrors && graphQLErrors[0]))
          return
        }
        const oldHeaders = operation.getContext().headers || {}
        operation.setContext({
          __didRefreshRetry: true,
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${newToken}`,
          },
        })
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        })
      })
      .catch((err) => observer.error(err))
  })
})

const client = new ApolloClient({
  link: ApolloLink.from([refreshOnUnauthorized, authLink, httpLink]),
  cache: new InMemoryCache(),
})

ReactDOM.render(
  <ApolloProvider client={client}>
    <Provider store={store}>
      <AuthProvider>
        <ToastContainer />
        <Router history={hist} basename={process.env.REACT_APP_BASEDIR}>
          <BrowserRouter>
            <Suspense fallback={<FallbackLoading />}>
              <Switch>
                <Route exact path="/" component={LoginPage} />
                <Route exact path="/auth/callback" component={AuthCallback} />
                <Protected path="/dashboard" component={WonELayout} />
                <Route component={NotFound} />
              </Switch>
            </Suspense>
          </BrowserRouter>
        </Router>
        <Loader />
      </AuthProvider>
    </Provider>
  </ApolloProvider>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
